import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  NumberInput,
  Stack,
  Switch,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { TaxDto } from './types';
import { useCreateTax, useUpdateTax } from './taxesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  tax: TaxDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  rate: number;
  isInclusive: boolean;
}

export function TaxFormDrawer({ opened, onClose, tax }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateTax();
  const update = useUpdateTax();
  const submitting = create.isPending || update.isPending;
  const editing = tax !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', rate: 0, isInclusive: false },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
      rate: (v) =>
        v < 0 || v > 100 ? t('taxes.form.rateRange') : null,
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: tax?.code ?? '',
      nameAr: tax?.nameAr ?? '',
      nameEn: tax?.nameEn ?? '',
      rate: tax?.rate ?? 0,
      isInclusive: tax?.isInclusive ?? false,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, tax]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      rate: values.rate,
      isInclusive: values.isInclusive,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: tax.taxId, request: payload });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('taxes.form.editTitle') : t('taxes.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('taxes.form.codeLabel')}
            description={t('taxes.form.codeHelp')}
            placeholder="VAT16"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('taxes.form.nameArLabel')}
            placeholder={t('taxes.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('taxes.form.nameEnLabel')}
            placeholder={t('taxes.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <NumberInput
            label={t('taxes.form.rateLabel')}
            description={t('taxes.form.rateHelp')}
            min={0}
            max={100}
            step={0.5}
            decimalScale={4}
            suffix="%"
            withAsterisk
            {...form.getInputProps('rate')}
          />
          <Switch
            label={t('taxes.form.inclusiveLabel')}
            description={t('taxes.form.inclusiveHelp')}
            checked={form.values.isInclusive}
            onChange={(e) => form.setFieldValue('isInclusive', e.currentTarget.checked)}
          />

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
